// Branded portfolio cover. Tries the configured cover image first; if it
// fails to load (e.g. the project's final assets are not yet on disk), falls
// back to an on-brand SVG frame using the project initials. Honest by default:
// no Unsplash, no stock photography, no false project imagery.
import React, { useState } from "react";

const initials = (title = "") =>
  title
    .replace(/[^a-z0-9 ]/gi, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "K";

const Placeholder = ({ title, type, category, accent = "yellow" }) => {
  const isYellow = accent === "yellow";
  return (
    <svg
      viewBox="0 0 1600 1000"
      preserveAspectRatio="xMidYMid slice"
      className="w-full h-full block"
      role="img"
      aria-label={`${title} placeholder`}
    >
      <defs>
        <pattern id="kdots" width="32" height="32" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.4" fill="rgba(255,255,255,0.06)" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="1600" height="1000" fill="#000000" />
      <rect x="0" y="0" width="1600" height="1000" fill="url(#kdots)" />

      {/* Centered initials block */}
      {isYellow ? (
        <rect x="640" y="340" width="320" height="320" fill="#FFC600" />
      ) : (
        <rect x="640" y="340" width="320" height="320" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
      )}
      <text
        x="800"
        y="540"
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="Poppins, sans-serif"
        fontWeight="900"
        fontSize="200"
        letterSpacing="-6"
        fill={isYellow ? "#000000" : "#FFC600"}
      >
        {initials(title)}
      </text>

      {/* Bottom accent strip */}
      <line x1="60" y1="900" x2="240" y2="900" stroke="#FFC600" strokeWidth="3" />
      <text x="280" y="908" fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="22" fill="rgba(255,255,255,0.55)" letterSpacing="3.6">
        KOSTAJA STUDIO
      </text>
      <text x="1540" y="908" textAnchor="end" fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="22" fill="rgba(255,255,255,0.55)" letterSpacing="3.6">
        {(type || "").toUpperCase()}
      </text>
    </svg>
  );
};

const PortfolioCover = ({ item, accent = "yellow", className = "" }) => {
  const [failed, setFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  // If a real client logo is on file, prefer it (with brand background).
  // Otherwise try the cover image. Otherwise render the branded placeholder.
  const hasLogo = item?.logo && !logoFailed;
  const hasCover = !hasLogo && item?.cover && !failed;

  return (
    <div className={`relative w-full h-full bg-black overflow-hidden ${className}`}>
      {hasLogo ? (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: item.logoBg || "#000000" }}
        >
          <img
            src={item.logo}
            alt={`${item.title} logo`}
            loading="lazy"
            onError={() => setLogoFailed(true)}
            className="max-w-[62%] max-h-[62%] object-contain"
          />
        </div>
      ) : null}

      {hasCover ? (
        <img
          src={item.cover}
          alt={`${item.title} cover`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : null}

      {!hasLogo && !hasCover ? (
        <Placeholder
          title={item?.title || "Project"}
          type={item?.type}
          category={item?.category}
          accent={accent}
        />
      ) : null}
    </div>
  );
};

export default PortfolioCover;
